import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { useLanguage } from "@/i18n";

const HERO_IMG = "https://images.unsplash.com/photo-1561394706-0f5b464eaa15?crop=entropy&cs=srgb&fm=jpg&q=80&w=1920";

const Hero = () => {
  const { t, scrollTo } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden grain">
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Costa di Isola delle Femmine" className="w-full h-[115%] object-cover" fetchpriority="high" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#F8F5F0] via-[#F8F5F0]/78 to-[#F8F5F0]/25" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#F8F5F0] to-transparent" />
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pb-20 sm:pb-28 pt-40 w-full">
        <motion.p
          data-testid="hero-eyebrow"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.7 }}
          className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-semibold text-[#C86D51] mb-6"
        >
          <MapPin className="w-4 h-4" />
          {t.hero.eyebrow}
        </motion.p>

        <h1 data-testid="hero-title" className="font-display tracking-tight leading-[1.05] text-[#0D253A] text-4xl sm:text-5xl lg:text-7xl max-w-4xl">
          {t.hero.lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 1 ? "italic text-[#1E5F74]" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.55 + i * 0.16, duration: 0.95, ease: [0.16, 1, 0.3, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8 }}
          className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-[#333D47]"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => scrollTo("prenota")}
            className="px-8 py-4 bg-[#0D253A] text-[#F8F5F0] text-sm font-semibold rounded-full hover:bg-[#C86D51] hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
          >
            {t.hero.ctaPrimary}
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => scrollTo("appartamento")}
            className="px-8 py-4 border border-[#0D253A]/30 text-[#0D253A] text-sm font-semibold rounded-full hover:border-[#C86D51] hover:text-[#C86D51] transition-colors duration-300"
          >
            {t.hero.ctaSecondary}
          </button>
        </motion.div>

        <motion.div
          data-testid="hero-stats"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-14 inline-flex divide-x divide-[#0D253A]/12 rounded-2xl bg-white/70 backdrop-blur-xl border border-[#0D253A]/10 shadow-[0_20px_50px_-20px_rgba(13,37,58,0.25)]"
        >
          {t.hero.stats.map((s, i) => (
            <div key={i} className="px-6 sm:px-9 py-5 text-center">
              <p className="font-display text-2xl sm:text-3xl text-[#0D253A]">{s.v}</p>
              <p className="text-xs uppercase tracking-[0.18em] text-[#5C6773] mt-1">{s.l}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollTo("capitoli")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-6 right-6 sm:right-12 z-10 hidden sm:flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#0D253A]/60 hover:text-[#C86D51] transition-colors"
        aria-label={t.hero.scroll}
      >
        {t.hero.scroll}
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown className="w-4 h-4" />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
